import {useEffect, useState} from "react";
import {Button, Form, Input, Select, Alert, Switch, Space} from "antd";
import {useTranslation} from "react-i18next";
import ApiService from "../../core/service/api-service";
import moment from "moment";

export default function PatientEditModal({studyRow, onSaved}) {
    const {t} = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [allStudies, setAllStudies] = useState(false);
    
    useEffect(() => {
        if (studyRow) {
            const birthdateStr = studyRow.birthdate 
                ? moment(studyRow.birthdate).format('YYYY-MM-DD') 
                : '';
            form.setFieldsValue({
                dicomPatientId: studyRow.dicomPatientId || '',
                name: studyRow.patientName || '',
                birthdate: birthdateStr,
                gender: studyRow.patientSex || undefined
            });
        }
    }, [studyRow, form]);
    
    const handleSubmit = async (values) => {
        setSaving(true);
        try {
            // Convert birthdate to YYYYMMDD format for DICOM
            let birthdateStr = null;
            if (values.birthdate) {
                // Remove any dashes/slashes and ensure YYYYMMDD format
                birthdateStr = values.birthdate.replace(/[-/]/g, '');
            }
            
            const data = {
                patientId: studyRow.patientId,
                name: values.name,
                dicomPatientId: values.dicomPatientId,
                birthdate: birthdateStr,
                gender: values.gender || null,
                allStudies: allStudies,
                studyId: studyRow.id
            };
            
            const response = await ApiService.Patient.UpdateAndCopyStudies(data);
            if (response.success) {
                onSaved && onSaved();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{minWidth: 400}}>
            <Alert
                message={t('Warning')}
                description={t('This will update patient information and copy all studies of this patient with new patient info in PACS.')}
                type="warning"
                showIcon
                style={{marginBottom: 16}}
            />
            
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="dicomPatientId"
                    label={t('Patient ID')}
                    rules={[{required: true, message: t('Please enter patient ID')}]}
                >
                    <Input placeholder={t('Patient ID')} />
                </Form.Item>
                
                <Form.Item
                    name="name"
                    label={t('Patient Name')}
                    rules={[{required: true, message: t('Please enter patient name')}]}
                >
                    <Input placeholder={t('Patient Name')} />
                </Form.Item>
                
                <Form.Item
                    name="birthdate"
                    label={t('Birth Date')}
                    extra={t('Format: YYYY-MM-DD (e.g. 1990-05-15)')}
                >
                    <Input 
                        placeholder="1990-01-31"
                    />
                </Form.Item>
                
                <Form.Item
                    name="gender"
                    label={t('Gender')}
                >
                    <Select placeholder={t('Select gender')} allowClear>
                        <Select.Option value="M">{t('Male')}</Select.Option>
                        <Select.Option value="F">{t('Female')}</Select.Option>
                        <Select.Option value="O">{t('Other')}</Select.Option>
                    </Select>
                </Form.Item>
                
                <Form.Item label={t('All Studies')}>
                    <Switch
                        checked={allStudies}
                        onChange={setAllStudies}
                    />
                </Form.Item>
                
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        {t('Update Patient')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
